import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedAdminRouteProps {
  children: ReactNode;
}

/**
 * ProtectedAdminRoute Component
 * 
 * Wraps admin-only pages to ensure:
 * 1. User is authenticated
 * 2. User has admin role
 * 3. Non-admin users are redirected to dashboard
 * 
 * Security: This is a client-side check for UX.
 * All admin operations must also verify admin status server-side via RLS policies.
 */
export const ProtectedAdminRoute = ({ children }: ProtectedAdminRouteProps) => {
  const { user, isLoading, isAdmin, userRole } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Log unauthorized access attempts for security monitoring
    if (!isLoading && user && !isAdmin) {
      console.warn('[Security] Non-admin user attempted to access admin route:', {
        userId: user.id,
        email: user.email,
        role: userRole,
        attemptedPath: location.pathname,
        timestamp: new Date().toISOString()
      });
    }
  }, [isLoading, user, isAdmin, userRole, location.pathname]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Überprüfe Berechtigungen...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to dashboard if authenticated but not admin
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // User is authenticated and has admin role - render protected content
  return <>{children}</>;
};
